import React, { FC, ReactNode, useState, useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { observer } from "mobx-react-lite"
import { Button, PageTitle, useIntl, useToast, ToastLevel, Icon } from "@metsooutotec/modes-react-components"
import customerStore from "features/organizations/customers/store/customer-store"
import ListView from "components/utils/list-view/list-view"
import { NavigationDeleteEntityOptions, NavigationModalOptions } from "components/utils/modal-helper"
import { Customer } from "../../models/customer-models"
import { itemViewDetailsContainerStyles } from "components/common-style/common-style"
import CustomerModal from "../../components/customer-modal"

const CustomerListView: FC = observer(() => {
    const { formatMessage } = useIntl()
    const { setMessage } = useToast()
    const navigate = useNavigate()
    const location = useLocation()
    const { customers } = customerStore
    const deleteParams: NavigationDeleteEntityOptions = location.state
    const { deleted: Deleted, entityName: DeletedCustomerName } = deleteParams || {}

    const [isModalOpen, setIsModalOpen] = useState(false)
    //if customerToEdit is undefinded the modal will open with empty fields to create a new customer.
    const [customerToEdit, setCustomerToEdit] = useState<Customer>()

    const template = [
        {
            headingKey: "headings.customer",
            cellFunc: (c: Customer) => c.name,
            isSearchable: true,
        },
        {
            headingKey: "headings.contact",
            cellFunc: (c: Customer) => c.contact,
            isSearchable: true,
        },
        {
            headingKey: "headings.metsoContact",
            cellFunc: (c: Customer) => c.metsoContact,
            isSearchable: true,
        },
        {
            headingKey: "headings.icon",
            cellFunc: (c: Customer) => (
                <Icon name='icon-edit-16' onClick={(event: React.MouseEvent<Element, MouseEvent>) => handleEditIconClick(event, c)} />
            ),
        },
    ]

    useEffect(() => {
        if (Deleted) {
            {
                showNotifier(`You have deleted customer '${DeletedCustomerName}'.`)
            }
        }
        window.history.replaceState({}, document.title)
    }, [Deleted, DeletedCustomerName, deleteParams])

    const showNotifier = (text: string) => {
        setMessage(text, ToastLevel.SUCCESS, 6000)
    }

    const customerModal = {
        open: () => {
            setIsModalOpen(true)
        },
        close: (passed: boolean, customerId?: string, navigationCustomerOptions?: NavigationModalOptions) => {
            setIsModalOpen(false)
            setCustomerToEdit(undefined)
            if (passed)
                navigate(`/customers/${customerId}`, {
                    state: navigationCustomerOptions,
                })
        },
    }

    const handleEditIconClick = (event: React.MouseEvent, customer: Customer) => {
        event.stopPropagation()
        setCustomerToEdit(customer)
        setIsModalOpen(true)
    }

    const rightPageButtons: ReactNode = (
        <>
            <Button icon='icon-plus-16' labelPosition='right' primary onClick={customerModal.open}>
                {formatMessage("customer.create")}
            </Button>
        </>
    )
    return (
        <>
            <PageTitle title={formatMessage("customerList.title")} extraItems={rightPageButtons} />
            <div style={itemViewDetailsContainerStyles}>
                <ListView
                    noneYetKey='customerList.noCustomersYet'
                    template={template}
                    data={customers?.slice().sort((a, b) => a?.name?.localeCompare(b?.name))}
                    onClickRow={(c: Customer) => navigate("/customers/" + c.id)}
                />
                <CustomerModal isOpen={isModalOpen} onClose={customerModal.close} customer={customerToEdit} />
            </div>
        </>
    )
})

export default CustomerListView
